import * as React from 'react';
import { TextField, InputAdornment, IconButton, Tooltip } from '@mui/material';
import { Search, FilterList, FilterListOff } from '@mui/icons-material';
import { setRaStore } from '../../utils/common';

/**
 * Props for the internal {@link MInputAdornment} helper.
 */
interface MInputAdornmentProps {
    /** Icon rendered inside the adornment button. */
    children: React.ReactNode;
    /** Tooltip shown when hovering the adornment button. */
    title?: string;
    /** Click handler for the adornment button. */
    handleClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const MInputAdornment: React.FC<MInputAdornmentProps> = ({ children, title, handleClick }) => {
    return (
        <InputAdornment position="end">
            <Tooltip title={title ?? ''}>
                <IconButton onClick={handleClick}>
                    {children}
                </IconButton>
            </Tooltip>
        </InputAdornment>
    );
};

/**
 * Props for {@link InputSearch}.
 */
interface InputSearchProps {
    /** React Admin resource name, used to persist the "isSearch" flag when {@link onSearch} is not provided. */
    resource?: string;
    /** React Admin `setFilters` callback, called with `{ ...defaultFilters, q: search }`. */
    setFilters: (filters: Record<string, unknown>) => void;
    /** Whether the advanced search panel is currently open. */
    isOpenSearch?: boolean;
    /** Toggles the advanced search panel and disables this quick-search field while it is open. */
    setIsOpenSearch: (isOpen: boolean) => void;
    /** Field names combined into the placeholder, e.g. `Search a or b`. */
    inputs?: string[];
    /** Extra filters merged alongside `q` when submitting the default search. */
    defaultFilters?: Record<string, unknown>;
    /**
     * Optional override invoked with the search text instead of the default
     * `setFilters` + `setRaStore` behavior.
     *
     * @param search - The trimmed search text.
     */
    onSearch?: (search: string) => void;
}

/**
 * A quick-search `TextField` with a search icon and a toggle for an advanced
 * search panel, submitting on Enter or search-icon click.
 */
const InputSearch: React.FC<InputSearchProps> = ({ resource, setFilters, isOpenSearch, setIsOpenSearch, inputs, defaultFilters, onSearch }) => {
    const [search, setSearch] = React.useState('');
    const [disabled, setDisabled] = React.useState(false);
    const [label, setLabel] = React.useState('Search');

    React.useEffect(() => {
        let name = '';
        (inputs || []).forEach((e) => {
            name += e + ' or ';
        });
        if (name) name = name.slice(0, -4);
        setLabel(`Search ${name}`);
    }, []);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
    };

    const handleSearch = (event: React.KeyboardEvent<HTMLInputElement> | React.MouseEvent<HTMLButtonElement>) => {
        const keyboardEvent = event as React.KeyboardEvent<HTMLInputElement>;
        const isSubmit = keyboardEvent.which === 13 || keyboardEvent.keyCode === 13 || event.type === 'click';
        if (isSubmit) {
            if (disabled) return;
            if (!search.trim()) return;
            if (onSearch) onSearch(search);
            else {
                setFilters({ ...(defaultFilters || {}), q: search });
                setRaStore(`${resource}.isSearch`, true);
            }
        }
    };

    const handleOpenSearch = () => {
        const isOpen = !isOpenSearch;
        setIsOpenSearch(isOpen);
        setDisabled(isOpen);
    };

    return (
        <TextField
            id="textSearch"
            name="q"
            placeholder={label}
            autoComplete="search"
            // fullWidth
            disabled={disabled}
            value={search}
            InputProps={{
                startAdornment: (
                    <MInputAdornment handleClick={handleSearch}>
                        <Search />
                    </MInputAdornment>
                ),
                endAdornment: (
                    <>
                        {!isOpenSearch && (
                            <MInputAdornment title={'Open advanced search'} handleClick={handleOpenSearch}>
                                <FilterList />
                            </MInputAdornment>
                        )}
                        {isOpenSearch && (
                            <MInputAdornment title={'Close advanced search'} handleClick={handleOpenSearch}>
                                <FilterListOff />
                            </MInputAdornment>
                        )}
                    </>
                )
            }}
            onChange={handleChange}
            onKeyPress={handleSearch}
        />
    );
};

export default React.memo(InputSearch);
